import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Printer, Share2, CheckCircle, Trash2, Save, Copy, CreditCard, RefreshCw, RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import LoadingState from '@/components/shared/LoadingState';
import ErrorState from '@/components/shared/ErrorState';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';

import OrderHeroCard from '@/components/orderDetail/OrderHeroCard';
import OrderTimeline from '@/components/orderDetail/OrderTimeline';
import OrderLinesTab from '@/components/orderDetail/OrderLinesTab';
import PaymentsTab from '@/components/orderDetail/PaymentsTab';
import InfoTab from '@/components/orderDetail/InfoTab';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';

const STATUS_MAP = { draft: 'مسودة', sent: 'مرسل', sale: 'مؤكد', cancel: 'ملغي', done: 'منتهي' };
const STATUS_COLOR = {
  draft: 'bg-amber-50 text-amber-700 border border-amber-200',
  sent:  'bg-blue-50 text-blue-700 border border-blue-200',
  sale:  'bg-emerald-50 text-emerald-700 border border-emerald-200',
  cancel:'bg-red-50 text-red-700 border border-red-200',
  done:  'bg-gray-100 text-gray-600 border border-gray-200',
};

const TABS = [
  { key: 'lines',    label: 'الأصناف' },
  { key: 'payments', label: 'الدفعات' },
  { key: 'info',     label: 'المعلومات' },
];

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('lines');
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // 'confirm'|'cancel'|'delete'

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [editLines, setEditLines] = useState([]);
  const [editNotes, setEditNotes] = useState('');
  const [editCustomer, setEditCustomer] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  const loadOrder = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await api.get(`/sales/orders/${parseInt(id)}`);
    if (res?.success && res.data) {
      setOrder(res.data);
    } else {
      setError(res?.error || 'فشل تحميل الطلب');
    }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadOrder(); }, [loadOrder]);

  // ── Edit mode helpers ─────────────────────────────────────────────────
  const enterEditMode = () => {
    if (!order || order.state !== 'draft') return;
    const customerId = Array.isArray(order.partner_id) ? order.partner_id[0] : null;
    const customerName = Array.isArray(order.partner_id) ? order.partner_id[1] : '';
    setEditCustomer(customerId ? { id: customerId, name: customerName } : null);
    setEditLines((order.lines || []).map(l => ({
      id: l.id,
      template_id: null,
      product_id: Array.isArray(l.product_id) ? l.product_id[0] : l.product_id,
      name: Array.isArray(l.product_id) ? l.product_id[1] : (l.name || ''),
      quantity: l.product_uom_qty,
      price_unit: l.price_unit,
      discount: l.discount || 0,
      subtotal: l.price_subtotal,
      isNew: false,
    })));
    setEditNotes(order.note || '');
    setDirty(false);
    setEditMode(true);
  };

  const exitEditMode = () => {
    if (dirty) { setShowLeaveDialog(true); return; }
    setEditMode(false);
  };

  // ── Actions ───────────────────────────────────────────────────────────
  const saveDraft = async () => {
    setSaving(true);
    const existingIds = (order.lines || []).map(l => l.id);
    const keptIds = editLines.filter(l => !l.isNew && l.id).map(l => l.id);
    const removedIds = existingIds.filter(id => !keptIds.includes(id));

    const lineCommands = [
      ...removedIds.map(lineId => [2, lineId, 0]),
      ...editLines.filter(l => !l.isNew && l.id).map(l => [1, l.id, { product_uom_qty: l.quantity, price_unit: l.price_unit, discount: l.discount || 0 }]),
      ...editLines.filter(l => l.isNew).map(l => [0, 0, { template_id: l.template_id, product_id: l.product_id, quantity: l.quantity, price_unit: l.price_unit, discount: l.discount || 0, name: l.name }]),
    ];

    const res = await api.put(`/sales/orders/${parseInt(id)}`, {
      fields: { partner_id: editCustomer?.id, note: editNotes },
      line_commands: lineCommands,
    });

    if (res?.success) {
      toast.success('تم حفظ التعديلات');
      setOrder(res.data);
      setEditMode(false); setDirty(false);
    } else {
      toast.error(res?.error || 'فشل الحفظ');
    }
    setSaving(false);
  };

  const confirmOrder = async () => {
    setSaving(true);
    try {
      const res = await api.post('/sellgo_v5_order_confirm', { order_id: parseInt(id) });
      if (res?.success) { toast.success('تم تأكيد الطلب'); await loadOrder(); setEditMode(false); }
      else toast.error(res?.error || 'فشل التأكيد');
    } catch (e) {
      toast.error(e?.message || 'فشل التأكيد');
    } finally {
      setSaving(false);
    }
  };

  const cancelOrder = async () => {
    setSaving(true);
    try {
      const res = await api.post(`/sales/orders/${parseInt(id)}/cancel`);
      if (res?.success) { toast.success('تم إلغاء الطلب'); await loadOrder(); setEditMode(false); }
      else toast.error(res?.error || 'فشل الإلغاء');
    } catch (e) {
      toast.error(e?.message || 'فشل الإلغاء');
    } finally {
      setSaving(false);
    }
  };

  const deleteOrder = async () => {
    setSaving(true);
    try {
      const res = await api.del(`/sales/orders/${parseInt(id)}`);
      if (res?.success) { toast.success('تم حذف الطلب'); navigate('/orders'); }
      else toast.error(res?.error || 'فشل الحذف');
    } catch (e) {
      toast.error(e?.message || 'فشل الحذف');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => navigate(`/orders/${parseInt(id, 10)}/print`);

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: order?.name, text: `أمر بيع ${order?.name} — ${order?.amount_total?.toLocaleString('ar-EG')} ${cur()}`, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('تم نسخ الرابط');
    }
  };

  const handleCopyOrder = () => {
    const partnerId = Array.isArray(order.partner_id) ? order.partner_id[0] : '';
    const partnerName = Array.isArray(order.partner_id) ? order.partner_id[1] : '';
    navigate(`/orders/new?customer_id=${partnerId}&customer_name=${encodeURIComponent(partnerName)}`);
  };

  const handleCollection = () => {
    const partnerId = Array.isArray(order.partner_id) ? order.partner_id[0] : '';
    const partnerName = Array.isArray(order.partner_id) ? order.partner_id[1] : '';
    navigate(`/collections/new?customer_id=${partnerId}&customer_name=${encodeURIComponent(partnerName)}`);
  };

  const handleReturn = () => navigate(`/returns/new?order_id=${parseInt(id, 10)}`);

  // ── Render ────────────────────────────────────────────────────────────
  if (loading) return <div className="p-4"><LoadingState message="جاري تحميل تفاصيل الطلب..." /></div>;
  if (error) return <div className="p-4"><ErrorState message={error} onRetry={loadOrder} /></div>;
  if (!order) return null;

  const isDraft = order.state === 'draft';
  const isSale = order.state === 'sale';
  const isDone = order.state === 'done';
  const isCancelled = order.state === 'cancel';

  return (
    <div className="flex flex-col min-h-full" dir="rtl">
      {/* ── Sticky Header ── */}
      <div className="sticky top-0 z-20 bg-card border-b border-border px-4 py-3 flex items-center gap-3 shadow-sm">
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <h1 className="text-lg font-bold text-foreground truncate">{order.name}</h1>
          <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border whitespace-nowrap flex-shrink-0 ${STATUS_COLOR[order.state] || 'bg-muted text-muted-foreground'}`}>
            {STATUS_MAP[order.state] || order.state}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={handlePrint} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-muted transition-colors">
            <Printer className="w-4 h-4 text-muted-foreground" />
          </button>
          <button onClick={handleShare} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-muted transition-colors">
            <Share2 className="w-4 h-4 text-muted-foreground" />
          </button>
          {isDraft && !editMode && (
            <Button onClick={enterEditMode} size="sm" variant="outline" className="border-primary text-primary rounded-xl px-3 h-8 text-xs">
              تعديل
            </Button>
          )}
          {editMode && (
            <Button onClick={exitEditMode} size="sm" variant="ghost" className="rounded-xl px-3 h-8 text-xs text-muted-foreground">
              إلغاء
            </Button>
          )}
        </div>
      </div>

      {/* ── Scrollable content ── */}
      <div className="flex-1 overflow-y-auto pb-32">
        <div className="p-4 space-y-4">

          {/* Edit mode banner */}
          {editMode && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-center gap-2 text-amber-700 text-sm">
              <Save className="w-4 h-4 flex-shrink-0" />
              <span>وضع التعديل</span>
              {dirty && <span className="mr-auto text-xs font-semibold">● تغييرات غير محفوظة</span>}
            </div>
          )}

          {/* Hero Card */}
          <OrderHeroCard order={order} />

          {/* Timeline */}
          <OrderTimeline order={order} />

          {/* Tabs */}
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {/* Tab bar */}
            <div className="flex border-b border-border">
              {TABS.map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 py-3 text-sm font-semibold transition-all relative ${
                    activeTab === tab.key ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.key && (
                    <motion.div layoutId="tab-indicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="p-4">
              <AnimatePresence mode="wait">
                <motion.div key={activeTab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
                  {activeTab === 'lines' && (
                    <OrderLinesTab
                      order={order}
                      editMode={editMode}
                      editLines={editLines}
                      setEditLines={setEditLines}
                      editNotes={editNotes}
                      setEditNotes={setEditNotes}
                      setDirty={setDirty}
                    />
                  )}
                  {activeTab === 'payments' && <PaymentsTab order={order} orderId={id} />}
                  {activeTab === 'info' && <InfoTab order={order} />}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* ── Sticky Action Bar ── */}
      <div className="fixed bottom-0 right-0 left-0 bg-card border-t border-border px-4 py-3 z-30 flex gap-2">
        {/* DRAFT */}
        {isDraft && editMode && (
          <>
            <Button onClick={() => setConfirmDialog('delete')} variant="outline" size="icon"
              className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl h-11 w-11 flex-shrink-0" disabled={saving}>
              <Trash2 className="w-4 h-4" />
            </Button>
            <Button onClick={saveDraft} variant="outline" disabled={saving || !dirty} className="flex-1 rounded-xl h-11 gap-1.5">
              <Save className="w-4 h-4" /> {saving ? 'جاري الحفظ...' : 'حفظ'}
            </Button>
            <Button onClick={() => setConfirmDialog('confirm')} disabled={saving || editLines.length === 0} className="flex-1 bg-primary rounded-xl h-11 gap-1.5">
              <CheckCircle className="w-4 h-4" /> تأكيد
            </Button>
          </>
        )}
        {isDraft && !editMode && (
          <>
            <Button onClick={() => setConfirmDialog('cancel')} variant="outline"
              className="flex-1 text-red-600 border-red-200 hover:bg-red-50 rounded-xl h-11" disabled={saving}>
              إلغاء الطلب
            </Button>
            <Button onClick={confirmOrder} disabled={saving} className="flex-1 bg-primary rounded-xl h-11 gap-1.5">
              <CheckCircle className="w-4 h-4" /> تأكيد الطلب
            </Button>
          </>
        )}

        {/* CONFIRMED */}
        {isSale && (
          <>
            <Button onClick={handleCollection} className="flex-1 bg-secondary rounded-xl h-11 gap-1.5">
              <CreditCard className="w-4 h-4" /> تحصيل
            </Button>
            <Button onClick={handlePrint} variant="outline" className="flex-1 rounded-xl h-11 gap-1.5">
              <Printer className="w-4 h-4" /> طباعة
            </Button>
            <Button onClick={handleShare} variant="outline" size="icon" className="rounded-xl h-11 w-11 flex-shrink-0">
              <Share2 className="w-4 h-4" />
            </Button>
            <Button onClick={handleReturn} variant="outline" size="icon" aria-label="مرتجع"
              className="rounded-xl h-11 w-11 flex-shrink-0 text-amber-600 border-amber-200 hover:bg-amber-50">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </>
        )}

        {/* DONE */}
        {isDone && (
          <>
            <Button onClick={handleCopyOrder} variant="outline" className="flex-1 rounded-xl h-11 gap-1.5">
              <Copy className="w-4 h-4" /> نسخ كطلب جديد
            </Button>
            <Button onClick={handlePrint} className="flex-1 bg-primary rounded-xl h-11 gap-1.5">
              <Printer className="w-4 h-4" /> طباعة
            </Button>
            <Button onClick={handleReturn} variant="outline" size="icon" aria-label="مرتجع"
              className="rounded-xl h-11 w-11 flex-shrink-0 text-amber-600 border-amber-200 hover:bg-amber-50">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </>
        )}

        {/* CANCELLED */}
        {isCancelled && (
          <>
            <Button onClick={handleCopyOrder} className="flex-1 bg-primary rounded-xl h-11 gap-1.5">
              <Copy className="w-4 h-4" /> نسخ كطلب جديد
            </Button>
            <Button onClick={loadOrder} variant="ghost" size="icon" className="rounded-xl h-11 w-11">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>

      {/* ── Confirm / Cancel / Delete Dialog ── */}
      <AlertDialog open={!!confirmDialog} onOpenChange={() => setConfirmDialog(null)}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDialog === 'confirm' && 'تأكيد الطلب'}
              {confirmDialog === 'cancel'  && 'إلغاء الطلب'}
              {confirmDialog === 'delete'  && 'حذف الطلب'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDialog === 'confirm' && 'سيتم تأكيد الطلب في Odoo ولا يمكن التراجع.'}
              {confirmDialog === 'cancel'  && 'هل تريد إلغاء الطلب نهائياً؟ لا يمكن التراجع.'}
              {confirmDialog === 'delete'  && 'هل أنت متأكد من حذف الطلب؟ لن يمكن استرجاعه.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction
              onClick={() => {
                const act = confirmDialog; setConfirmDialog(null);
                if (act === 'confirm') confirmOrder();
                else if (act === 'cancel') cancelOrder();
                else if (act === 'delete') deleteOrder();
              }}
              className={confirmDialog === 'confirm' ? 'bg-primary text-white' : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'}
            >
              {confirmDialog === 'confirm' && 'تأكيد'}
              {confirmDialog === 'cancel'  && 'إلغاء الطلب'}
              {confirmDialog === 'delete'  && 'حذف'}
            </AlertDialogAction>
            <AlertDialogCancel>تراجع</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Leave without saving ── */}
      <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle>تغييرات غير محفوظة</AlertDialogTitle>
            <AlertDialogDescription>لديك تغييرات غير محفوظة. هل تريد الخروج بدون حفظ؟</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction onClick={() => { setShowLeaveDialog(false); setEditMode(false); setDirty(false); }}
              className="bg-destructive text-destructive-foreground">خروج بدون حفظ</AlertDialogAction>
            <AlertDialogCancel>البقاء</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}